import React, { useState, useEffect } from "react";
import Media from "react-media";

/** @type {Object.<string, string>} */
const SCREEN_SIZES = {
  mini: "(max-width: 320px)",
  small: "(min-width: 321px) and (max-width: 375px)",
  medium: "(min-width: 376px) and (max-width: 768px)",
  large: "(min-width: 769px) and (max-width: 1024px)",
  max: "(min-width: 1025px)",
};

/**
 * Custom hook to determine if the current screen size matches the given sizes
 * @param {Array<'mini'|'small'|'medium'|'large'|'max'>} size - Array of screen sizes to check
 * @returns {boolean} Whether the current screen size matches any of the given sizes
 */
export const useScreenSize = (size) => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener("resize", handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  if (windowSize.width <= 320) {
    return size.includes("mini");
  }

  if (windowSize.width <= 375) {
    return size.includes("small");
  }

  if (windowSize.width <= 768) {
    return size.includes("medium");
  }

  if (windowSize.width <= 1024) {
    return size.includes("large");
  }

  return size.includes("max");
};

/**
 * Component that only renders children if the screen size matches one of the given sizes
 * @param {Object} props
 * @param {Array<'mini'|'small'|'medium'|'large'|'max'>} props.sizes - Array of screen sizes to check
 * @param {React.ReactNode} props.children - Content to render when screen size matches
 * @returns {React.ReactElement}
 */
export const Screen = ({ sizes, children }) => (
  <Media queries={SCREEN_SIZES}>
    {(matches) => {
      const shouldShow = sizes.reduce((result, value) => {
        return result || matches[value];
      }, false);
      return <>{Boolean(shouldShow) && children}</>;
    }}
  </Media>
);

/**
 * Component that renders children for screens larger than (and including) the given size
 * @param {Object} props
 * @param {'mini'|'small'|'medium'|'large'|'max'} props.size - Screen size breakpoint
 * @param {React.ReactNode} props.children - Content to render when condition is met
 * @returns {React.ReactElement}
 */
export const ScreenLT = ({ size, children }) => {
  const allKeys = Object.keys(SCREEN_SIZES);
  const sizeIndex = allKeys.indexOf(size);
  const keys = allKeys.slice(sizeIndex);

  return (
    <Media queries={SCREEN_SIZES}>
      {(matches) => {
        const shouldShow = keys.reduce((result, value) => {
          return result || matches[value];
        }, false);

        return <>{Boolean(shouldShow) && children}</>;
      }}
    </Media>
  );
};

/**
 * Component that renders children for screens smaller than (and including) the given size
 * @param {Object} props
 * @param {'mini'|'small'|'medium'|'large'|'max'} props.size - Screen size breakpoint
 * @param {React.ReactNode} props.children - Content to render when condition is met
 * @returns {React.ReactElement}
 */
export const ScreenST = ({ size, children }) => {
  const allKeys = Object.keys(SCREEN_SIZES);
  const sizeIndex = allKeys.indexOf(size);
  const keys = allKeys.slice(0, sizeIndex + 1);

  return (
    <Media queries={SCREEN_SIZES}>
      {(matches) => {
        const shouldShow = keys.reduce((result, value) => {
          return result || matches[value];
        }, false);

        return <>{Boolean(shouldShow) && children}</>;
      }}
    </Media>
  );
};
